import React from "react";
import { Chip, Box, Link, Typography } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { ILicense2 } from "../../../api/Account/myLicensesApi";
import WarningIcon from "@mui/icons-material/Warning";
import {
  commonDataGridStyles,
  defaultDataGridProps,
} from "../../../styles/dataGridStyles";

export interface IMyLicensesGridProps {
  rows: ILicense2[];
  onOpenDialog: (row: ILicense2) => void;
}

const MyLicensesGrid: React.FC<IMyLicensesGridProps> = ({
  rows,
  onOpenDialog,
}) => {
  const columns: GridColDef[] = [
    {
      field: "productName",
      headerName: "Application",
      flex: 1,
      minWidth: 460,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              whiteSpace: "normal", // Allow text wrapping
              lineHeight: 1.5,
            }}
          >
            <Link
              href="#"
              color={row.isCanceled === true ? "secondary" : "primary"}
              onClick={() => onOpenDialog(row)}
              variant="inherit"
              sx={{ textDecoration: "none" }}
            >
              {row.isExternal ? (
                <div>{`${row.programName} (${row.userName})`}</div>
              ) : (
                <div>{row.programName}</div>
              )}
            </Link>
            <Box
              sx={{
                display: "flex",
                alignItems: "center", // vertically center
                gap: 1, // spacing between buttons
              }}
            >
              {row.isOrphan ? (
                <WarningIcon color="error" fontSize="small" />
              ) : (
                <div></div>
              )}
              <Typography variant="caption">{row.licenseKey}</Typography>
            </Box>
          </Box>
        );
      },
    },
    { field: "branchName", headerName: "Branch", width: 150 },
    { field: "station", headerName: "Station", width: 120 },
    {
      field: "date",
      headerName: "Date",
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        params.row.date ? format(new Date(params.row.date), "MMMM/yyyy") : ""
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 220,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        /*  
            None,
            Requested,
            Active,
            Rejected,
            UninstallRequest,
            Uninstalled,
            NotFound
        */
        if (row.Status === 1 || row.status === 2) {
          return <Chip color="primary" label="Active" />;
        }
        if (row.Status === 4 || row.status === 5) {
          return <Chip label="Uninstalled" />;
        }
        return <Chip color="secondary" label="Unknown" />;
      },
    },
  ];

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.licenseId || row.id}
        sx={{
          ...commonDataGridStyles,
          "& .MuiDataGrid-row": {
            minHeight: "100px", // Increase row height for more spacing
          },
          "& .MuiDataGrid-cell": {
            padding: "8px 16px", // Reduce vertical padding, keep horizontal
            display: "flex",
            alignItems: "center", // Center content vertically
          },
          "& .MuiDataGrid-columnHeaders": {
            minHeight: "60px", // Increase header height
          },
          "& .MuiDataGrid-columnHeader": {
            padding: "8px 16px", // Reduce header vertical padding
            display: "flex",
            alignItems: "center", // Center header text vertically
          },
        }}
        {...defaultDataGridProps}
        slots={{ toolbar: GridToolbar }}
        disableRowSelectionOnClick
        initialState={{
          ...defaultDataGridProps.initialState,
          sorting: {
            sortModel: [{ field: "date", sort: "desc" }],
          },
          columns: {
            columnVisibilityModel: {
              userId: false,
            },
          },
        }}
      />
    </Box>
  );
};

export default MyLicensesGrid;
